<template>
  <div class="h-full">
    <UIFloatingActionButton
      v-if="!isPastEvent && canManageGuests"
      v-show="FABIsVisible && !guestIsInitialLoading"
      :label-is-visible="FABLabelIsVisible"
      class="bg-secondary z-2000 laptop:hidden"
      @click="onGuestAddPress"
    >
      <template slot="icon">
        <FontAwesomeIcon
          :icon="['far', 'plus']"
          class="fab-icon text-xl text-white"
        />
      </template>
      <span class="text-white">
        Add guest
      </span>
    </UIFloatingActionButton>

    <div class="h-full flex flex-col">
      <div class="sticky top-header p-20 bg-gray-100 border-b border-gray-300 z-2000 overflow-hidden tablet:py-25
      tablet:px-30">
        <p
          v-if="currentEvent && isPastEvent"
          class="archived-event-banner flex items-center text-sm text-primary mb-12"
        >
          <FontAwesomeIcon
            :icon="['fad', 'clipboard-list-check']"
            class="flex-shrink-0 text-base text-gray-600 mr-8"
          />
          <span>
            This event is archived. Guests can no longer be checked in or changed, but you can still export them.
          </span>
        </p>
        <p
          v-if="sharedEventLabel"
          :title="sharedEventTooltip"
          class="shared-event-banner flex items-center text-sm text-primary mb-12"
        >
          <FontAwesomeIcon
            :icon="['fad', 'user-friends']"
            class="flex-shrink-0 text-base text-secondary mr-8"
          />
          <span class="truncate">
            {{ sharedEventLabel }}
            <span class="font-semibold text-secondary-dark">· {{ currentUserRoleLabel }}</span>
          </span>
        </p>
        <div class="flex items-end justify-between mb-15">
          <ElPopover
            placement="bottom-start"
            width="250"
            popper-class="guests-count-overview"
            trigger="hover"
          >
            <span
              slot="reference"
              class="flex-shrink-0 text-primary-light leading-none"
            >
              <span class="inline-block text-4xl touch:text-3xl">
                {{ totalGuestCountLabel }}
              </span>
              <span class="text-lg touch:text-base">
                {{ totalGuestCountLabel === 1 ? 'guest' : 'guests' }}
              </span>
              <FontAwesomeIcon
                :icon="['fas', 'info-circle']"
                class="text-xl text-primary align-top mt-4 touch:text-xl"
              />
            </span>
            <h3 class="font-medium text-primary-light mb-10">Overview</h3>
            <span>{{ guestCountDetails }}</span>
          </ElPopover>
          <div class="flex-shrink-0">
            <button
              v-if="totalGuestCountLabel && canManageGuests"
              v-show="visibleButtons.includes('export')"
              :disabled="guestsIsEmpty"
              class="font-semibold text-primary py-8 px-20 border-2 border-primary rounded-8 focus:outline-none
              mobile-m:inline mobile-l:px-25 laptop-l:px-35"
              :class="{
                'mr-10': visibleButtons.length === 2 && !isPastEvent,
                'focus:bg-gray-300 hover:bg-gray-300': !guestsIsEmpty,
                'cursor-not-allowed opacity-75': guestsIsEmpty,
              }"
              @click="onGuestExportPress"
            >
              Export
            </button>
            <button
              v-if="!isPastEvent && canManageGuests"
              v-show="visibleButtons.includes('import')"
              class="text-white tracking-wide py-8 px-20 bg-primary border-2 border-primary rounded-8 focus:outline-none
              focus:opacity-75 hover:opacity-75 mobile-l:px-25 laptop:mr-10 laptop-l:px-30"
              @click="onGuestImportPress"
            >
              Import
              <span class="hidden tablet:inline">
                guests
              </span>
            </button>
            <button
              v-if="!isPastEvent && canManageGuests"
              class="hidden text-white tracking-wide bg-secondary py-8 px-40 border-2 border-secondary rounded-8
              focus:outline-none focus:opacity-75 hover:opacity-75 laptop:inline laptop-l:px-45"
              @click="onGuestAddPress"
            >
              Add guest
            </button>
          </div>
        </div>
        <GuestSearch
          class="w-full"
          :first-name-first-order="this.firstNameFirstOrder"
          @guest-update="onGuestUpdate($event)"
        />
      </div>

      <GuestList
        :guests="filteredGuests.guests"
        :list-item-height="64"
        :read-only="!canManageGuests || isPastEvent"
        :check-in-disabled="isPastEvent"
        class="flex-1 flex flex-col z-10"
        @guest-update="onGuestUpdate($event)"
        @guest-delete="onGuestDelete($event)"
        @guest-check-in="checkInGuest($event)"
        @guest-reset-check-in="resetGuestCheckIn($event)"
        @guest-extras="onGuestExtras($event)"
      />
      <GuestExtrasCheckInDialog
        :visible="extrasDialog.visible"
        :guest="extrasDialogGuest"
        :saving="extrasDialog.saving"
        @close="extrasDialog.visible = false"
        @save="updateGuestCheckedInExtras($event)"
      />
      <GuestModal
        v-model="modal.showing"
        :type="modal.type"
        :loading="modal.loading"
        :event="currentEvent"
        :first-name-first-order="this.firstNameFirstOrder"
        :existing-guest="modalGuestData"
        :badge-support="currentEvent && currentEvent.isSupportingBadge"
        :qr-code-support="currentEvent && currentEvent.isSupportingQRCode"
        :barcode-value-support="currentEvent && !!currentEvent.guestIdFieldForCheckIn"
        :max-additional-guests="maxAdditionalGuests"
        :can-upgrade="isEventOwner"
        @hidden="modal.loading = false"
        @upgrade-event="onUpgradeEvent"
        @guest-update="onGuestUpdate($event)"
        @guest-delete="onGuestDelete($event)"
        @add-guest="addGuest($event)"
        @update-guest="updateGuest($event)"
        @delete-guest="deleteGuest($event)"
      />
      <GuestExport :event="currentEvent" />
      <GuestImport :event="currentEvent" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Compressor from 'compressorjs';
import { v4 as UUID } from 'uuid';
import { FBModule, FBStorageRef } from '@/helpers/firebaseServices';
import { eventGuestStoragePath } from '@/helpers/cloudStorageRefs';
import {
  FSGetUserEvent, FSGetUserTeamMembers, FSGetEventGuests, FSGetEventGuest, FSGetEventMember,
} from '@/helpers/firestoreRefs';
import { showErrorNotification } from '@/utils/notification';
import { normalizePhoneNumberOrKeep } from '@/utils/phone';
import guestPluralRules from '@/utils/pluralRules/guest';
import extraGuestPluralRules from '@/utils/pluralRules/extraGuest';
import windowSize from '@/mixins/windowSize';
import windowScrollPosition from '@/mixins/windowScrollPosition';
import UIFloatingActionButton from '@/components/UI/UIFloatingActionButton';
import GuestModal from '@/components/guest/GuestModal';
import GuestSearch from '@/components/guest/GuestSearch';
import GuestList from '@/components/guest/list/GuestList';
import GuestExport from '@/components/guest/export/GuestExport';
import GuestImport from '@/components/guest/import/GuestImport';
import GuestExtrasCheckInDialog from '@/components/guest/GuestExtrasCheckInDialog';
import guestModule from '@/store/modules/guest';

const mobileSBreakpoint = 320; // TODO: Take from TailwindCSS config
const mobileLargeMinWidth = 425;

function normalizeAddGuestData(guestData) {
  return {
    ...guestData.firstName !== undefined && { firstName: `${guestData.firstName}` },
    ...guestData.lastName !== undefined && { lastName: `${guestData.lastName}` },
    ...guestData.photoStoragePath && { photoStoragePath: `${guestData.photoStoragePath}` },
    ...guestData.email && { email: `${guestData.email}` },
    ...guestData.status !== undefined && { status: parseInt(guestData.status, 10) },
    ...guestData.vip !== undefined && { vip: !!guestData.vip },
    ...guestData.totalExtraGuests !== undefined && { totalExtraGuests: parseInt(guestData.totalExtraGuests, 10) },
    ...guestData.phoneNumber && { phone: normalizePhoneNumberOrKeep(guestData.phoneNumber) || `${guestData.phoneNumber}` },
    ...guestData.jobTitle && { jobPosition: `${guestData.jobTitle}` },
    ...guestData.organization && { company: `${guestData.organization}` },
    ...guestData.note && { notes: `${guestData.note}` },
    // eslint-disable-next-line max-len
    ...guestData.attendingTime && typeof guestData.attendingTime.toDate === 'function' && { attendingTime: guestData.attendingTime.toDate() },
    ...guestData.checkInTime && typeof guestData.checkInTime.toDate === 'function' && { checkInTime: guestData.checkInTime.toDate() },
    ...guestData.checkOutTime && typeof guestData.checkOutTime.toDate === 'function' && { checkOutTime: guestData.checkOutTime.toDate() },
    ...guestData.createdBy && { createdBy: `${guestData.createdBy}` },
    ...guestData.barcodeValue !== undefined && guestData.barcodeValue !== '' && { barcodeValue: guestData.barcodeValue },
  };
}

function cleanUpdateGuestData(oldGuestData, guestData) { // Removes fields that don't need updates
  const normalizedObj = {};

  if (guestData?.firstName && oldGuestData?.firstName !== guestData.firstName) {
    normalizedObj.firstName = guestData.firstName;
  }
  if (guestData?.lastName && oldGuestData?.lastName !== guestData.lastName) {
    normalizedObj.lastName = guestData.lastName;
  }
  if (oldGuestData?.photoStoragePath !== guestData.photoStoragePath) {
    normalizedObj.photoStoragePath = guestData.photoStoragePath;
  }
  if (guestData?.email && oldGuestData?.email !== guestData.email) {
    normalizedObj.email = guestData.email;
  }
  if (guestData?.status !== undefined && oldGuestData?.status !== guestData.status) {
    normalizedObj.status = guestData.status;
  }
  if (guestData?.vip !== undefined && oldGuestData?.vip !== guestData.vip) {
    normalizedObj.vip = guestData.vip;
  }
  if (guestData?.totalExtraGuests !== undefined && oldGuestData?.totalExtraGuests !== guestData.totalExtraGuests) {
    normalizedObj.totalExtraGuests = guestData.totalExtraGuests;
  }
  if (guestData?.phoneNumber) {
    const phoneNumber = normalizePhoneNumberOrKeep(guestData.phoneNumber) || guestData.phoneNumber;

    if (oldGuestData?.phoneNumber !== phoneNumber) normalizedObj.phoneNumber = phoneNumber;
  }
  if (guestData?.jobTitle && oldGuestData?.jobTitle !== guestData.jobTitle) {
    normalizedObj.jobTitle = guestData.jobTitle;
  }
  if (guestData?.organization && oldGuestData?.organization !== guestData.organization) {
    normalizedObj.organization = guestData.organization;
  }
  if (guestData?.note && oldGuestData?.note !== guestData.note) {
    normalizedObj.note = guestData.note;
  }
  if (guestData?.barcodeValue !== undefined && oldGuestData?.barcodeValue !== guestData.barcodeValue) {
    normalizedObj.barcodeValue = guestData.barcodeValue;
  }

  return normalizedObj;
}

function prepareGuestDataForFB(guestData) {
  const normalizedObj = { ...guestData };

  if (guestData.phoneNumber) {
    normalizedObj.phone = normalizePhoneNumberOrKeep(guestData.phoneNumber) || guestData.phoneNumber;
    delete normalizedObj.phoneNumber;
  }
  if (guestData.jobTitle) {
    normalizedObj.jobPosition = guestData.jobTitle;
    delete normalizedObj.jobTitle;
  }
  if (guestData.organization) {
    normalizedObj.company = guestData.organization;
    delete normalizedObj.organization;
  }
  if (guestData.note) {
    normalizedObj.notes = guestData.note;
    delete normalizedObj.note;
  }

  return normalizedObj;
}

export default {
  props: {
    eventID: {
      type: String,
      default: '',
    },
  },
  components: {
    UIFloatingActionButton,
    GuestModal,
    GuestSearch,
    GuestList,
    GuestExport,
    GuestImport,
    GuestExtrasCheckInDialog,
  },
  mixins: [ // TODO: Replace with Composition API or CSS only button
    windowSize('windowSize', 50, { maxWait: 50 }),
    windowScrollPosition('windowScrollPosition', 20, { leading: true, maxWait: 50 }),
  ],
  beforeRouteLeave(to, from, next) {
    // Intercept route push when modal is open, to close it instead of navigating to the new route
    if (this.modal.showing && !this.noRouteCapture) {
      this.modal.showing = false;
      return;
    }

    next();
  },
  data: () => ({
    extrasDialog: { visible: false, guestId: null, saving: false },
    // Role of the signed-in user from events/{eventId}/members/{uid}; null until loaded or when there is no document
    memberRole: null,
    unsubEventMember: null,
    unsubUserEvent: null,
    unsubUserTeamMembers: null,
    unsubEventGuestsRef: null,
    replaceExistingTeamMembers: true,
    FABIsVisible: true,
    FABLabelIsVisible: false,
    previousWindowScrollPositionY: 0,
    showGuestDialog: false,
    modal: {
      showing: false,
      loading: false,
      type: 'guest-add', // One modal at any point in time
      guestId: '',
    },
    firstNameFirstOrder: false,
    userEventLoadedInit: false,
    noRouteCapture: false, // Set to true when routing in the modal
    unwatchEventFirstNameFirstOrder: null,
  }),
  created() {
    if (!this.$store.state.guest) this.$store.registerModule('guest', guestModule);

    this.unsubUserEvent = this.subUserEvent(this.eventID);
    this.unsubEventMember = FSGetEventMember(this.eventID, this.$store.state.user.auth.authData.id)
      .onSnapshot((doc) => {
        this.memberRole = doc.exists ? (doc.data().role || null) : null;
      }, (error) => {
        this.memberRole = null;
        console.error(error);
      });
    this.unsubUserTeamMembers = this.subUserTeamMembers(this.$store.state.user.auth.authData.id);

    // Watch current event
    // eslint-disable-next-line func-names
    this.unwatchEventFirstNameFirstOrder = this.$watch(function () {
      // Watching eventLoadingStatus and event's firstNameFirstOrder as the normal watcher
      // won't trigger (happens in the same tick)
      if (this.eventLoadingStatus !== 'snapshot-loaded') return null; // Return null to trigger watcher

      return this.allEvents[this.eventID]?.firstNameFirstOrder;
      // eslint-disable-next-line func-names
    }, function (value) {
      if (this.eventLoadingStatus !== 'snapshot-loaded') return;

      const firstNameFirstOrder = Boolean(value);

      if (firstNameFirstOrder === this.firstNameFirstOrder && Boolean(this.unsubEventGuestsRef)) return;
      this.firstNameFirstOrder = firstNameFirstOrder;

      // Unsubscribe previous snapshot listener
      if (this.unsubEventGuestsRef !== null) this.unsubEventGuestsRef();

      // Resetting guests and filtered guests state
      this.$store.dispatch('guest/resetGuestState');
      this.$store.dispatch('guest/filter/resetGuestFilterState');

      this.unsubEventGuestsRef = FSGetEventGuests(this.eventID)
        .orderBy(value ? 'firstName' : 'lastName', 'asc')
        .orderBy(value ? 'lastName' : 'firstName', 'asc')
        .onSnapshot(async (snapshot) => {
          await this.onGuestsSnapshotChange({
            snapshot,
            firstNameFirstOrder,
          });
          this.updateGuestIsInitialLoading(false);
        }, (error) => {
          this.updateGuestIsInitialLoading(false);
          console.error(error);
        });
    }, { immediate: true });
  },
  watch: {
    'currentEvent.title': {
      immediate: true,
      handler(value) {
        this.$store.commit('navigation/SET_CURRENT_NAVIGATION_TITLE_BASE', value || '');
      },
    },
    'windowSize.width': {
      immediate: true,
      handler(value) {
        this.FABLabelIsVisible = value > mobileLargeMinWidth;
      },
    },
    'windowScrollPosition.y': {
      immediate: true,
      handler(value) {
        if (this.windowSize.width > mobileLargeMinWidth) {
          this.FABIsVisible = true;
          this.FABLabelIsVisible = value <= this.previousWindowScrollPositionY;
          this.previousWindowScrollPositionY = value;

          return;
        }

        this.FABIsVisible = value <= this.previousWindowScrollPositionY || this.previousWindowScrollPositionY <= 0;
        this.previousWindowScrollPositionY = value;
      },
    },
    maxAdditionalGuests: {
      handler(value) {
        if (value === 0 && this.modal.showing && this.modal.type === 'guest-add') {
          this.modal.type = 'guest-add-limit-reached';
        }
      },
    },
  },
  computed: {
    ...mapState('user/events', {
      eventLoadingStatus: 'loadingStatus',
      allEvents: 'all',
    }),
    ...mapGetters('user/auth', {
      userId: 'authDataId',
    }),
    ...mapGetters('guest', {
      guestsIsEmpty: 'guestsIsEmpty',
    }),
    ...mapGetters('guest/filter', {
      filteredGuests: 'filteredGuests',
    }),
    guestIsInitialLoading() {
      return this.$store.state?.guest?.isInitialLoading ?? true;
    },
    currentEvent() {
      if (!this.eventID || !this.allEvents[this.eventID]) return null;
      return this.allEvents[this.eventID];
    },
    maxAdditionalGuests() {
      if (this.currentEvent?.maxGuests && this.$store.state?.guest?.guestsCounts) {
        return Math.max(this.currentEvent.maxGuests
          - (this.$store.state.guest.guestsCounts.mainGuest + this.$store.state.guest.guestsCounts.extraGuest), 0);
      }
      return 0;
    },
    totalGuestCountLabel() {
      return this.filteredGuests.guestsCounts.mainGuest + this.filteredGuests.guestsCounts.extraGuest;
    },
    guestCountDetails() {
      return `${guestPluralRules(this.filteredGuests.guestsCounts.mainGuest)}
        ${this.filteredGuests.guestsCounts.extraGuest ? `(+${extraGuestPluralRules(this.filteredGuests.guestsCounts.extraGuest)})` : ''}`;
    },
    // Same resolution as the Android app (EventRoleExtensions.resolveRole): a members/{uid} document wins, the owner
    // is eventOwner, any other team member without a document gets check-in rights only. Until the member document
    // has loaded a non-owner is treated as check-in only, so manage actions never flash for them.
    currentUserRole() {
      if (!this.currentEvent || !this.userId) return null;
      if (this.currentEvent.ownerId === this.userId) return 'eventOwner';
      if (this.memberRole) return this.memberRole;
      return 'guestsCheckIn';
    },
    canManageGuests() {
      return Boolean(this.currentUserRole) && this.currentUserRole !== 'guestsCheckIn';
    },
    currentUserRoleLabel() {
      return {
        eventOwner: 'Owner',
        guestsFullAccess: 'Manager access',
        guestsCheckIn: 'Check-in only access',
        eventPromoter: 'Promoter access',
      }[this.currentUserRole] || 'Team member';
    },
    sharedEventOwner() {
      const owner = this.currentEvent?.owner || {};
      return owner.email || owner.name || 'another user';
    },
    sharedEventLabel() {
      if (!this.currentEvent || this.currentUserRole === 'eventOwner') return '';

      return `Shared with you by ${this.sharedEventOwner}`;
    },
    sharedEventTooltip() {
      const owner = this.currentEvent?.owner || {};
      const who = owner.name && owner.email ? `${owner.name} (${owner.email})` : this.sharedEventOwner;
      if (this.canManageGuests) return `${who} shared this event with you.`;

      return `${who} shared this event with you. You can check guests and their extra guests in and out, `
        + 'but not add, import, export, edit or delete guests.';
    },
    visibleButtons() {
      if (this.windowSize.width <= mobileSBreakpoint) {
        if (this.currentEvent?.status === 1) return ['export'];
        return ['import'];
      }
      return ['export', 'import'];
    },
    isEventOwner() {
      return !!this.currentEvent && this.currentEvent.ownerId === this.userId;
    },
    isPastEvent() {
      return !this.currentEvent || this.currentEvent?.status === 1;
    },
    extrasDialogGuest() {
      if (!this.extrasDialog.guestId) return null;

      return this.$store.state?.guest?.guests?.find((guest) => guest?.id === this.extrasDialog.guestId) || null;
    },
    modalGuestData() {
      if (!this.$store.state?.guest?.guests) return undefined;
      return this.$store.state.guest.guests.find((guest) => guest?.id === this.modal.guestId);
    },
  },
  methods: {
    ...mapActions('navigation', {
      updateCurrentNavigationTitleBase: 'updateCurrentNavigationTitleBase',
    }),
    ...mapActions('user/teamMembers', {
      onTeamMembersSnapshot: 'onSnapshot',
    }),
    ...mapActions('guest', {
      updateGuestIsInitialLoading: 'updateIsInitialLoading',
      onGuestsSnapshotChange: 'onGuestsSnapshotChange',
    }),
    ...mapActions('guest/export', {
      updateGuestExportDialogIsVisible: 'updateDialogIsVisible',
    }),
    ...mapActions('guest/import', {
      updateGuestImportDialogIsVisible: 'updateDialogIsVisible',
    }),
    ...mapActions('user/events', {
      onUserEventSnapshot: 'onEventSnapshot',
    }),
    subUserEvent(eventId) {
      return FSGetUserEvent(eventId)
        .onSnapshot((eventSnapshot) => {
          this.onUserEventSnapshot(eventSnapshot);
        }, (error) => {
          console.error(error);
        });
    },
    subUserTeamMembers(userId) {
      return FSGetUserTeamMembers(userId)
        .orderBy('name')
        .onSnapshot((snapshot) => {
          this.onTeamMembersSnapshot({
            snapshot,
            replaceExistingState: this.replaceExistingTeamMembers,
          });

          // Don't replace the team members on subsequent snapshots
          this.replaceExistingTeamMembers = false;
        }, (error) => {
          console.error(error);
        });
    },
    onGuestExportPress() {
      if (!this.canManageGuests) return;
      this.updateGuestExportDialogIsVisible(true);
    },
    onGuestImportPress() {
      if (!this.canManageGuests) return;
      this.updateGuestImportDialogIsVisible(true);
    },
    onUpgradeEvent() {
      // Shared events: only the owner can pay for an upgrade
      if (!this.isEventOwner) return;
      this.noRouteCapture = true;
      this.$router.push({ name: 'EventUpgrade', params: { eventID: this.eventID } });
    },
    onGuestAddPress() {
      if (!this.canManageGuests) return;
      this.modal.showing = true;
      this.modal.guestId = '';

      if (this.maxAdditionalGuests) {
        // Show add guest modal
        this.modal.type = 'guest-add';
        return;
      }

      // Show max guests limit modal
      this.modal.type = 'guest-add-limit-reached';
    },
    onGuestUpdate(guestId) {
      if (!this.canManageGuests || this.isPastEvent) return;
      this.modal.type = 'guest-update';
      this.modal.showing = true;
      this.modal.guestId = guestId;
    },
    onGuestDelete(guestId) {
      if (!this.canManageGuests || this.isPastEvent) return;
      this.modal.type = 'guest-delete';
      this.modal.showing = true;
      this.modal.guestId = guestId;
    },
    // Quick check-in from the list row, same document changes as picking "Checked-in" in the guest modal
    async checkInGuest(guestId) {
      const guest = this.$store.state?.guest?.guests?.find((item) => item?.id === guestId);
      if (!guest || guest.status === 2 || this.isPastEvent) return;

      try {
        const nowTimestamp = FBModule.firestore.Timestamp.fromDate(new Date());

        await FSGetEventGuest(this.eventID, guest.id).update({
          status: 2,
          checkInType: 3,
          modificationTime: nowTimestamp,
          ...!guest.checkInTime && { checkInTime: nowTimestamp },
          ...guest.checkOutTime && { checkOutTime: FBModule.firestore.FieldValue.delete() },
        });
      } catch (error) {
        showErrorNotification(error.message);
        console.error(error);
      }
    },
    onGuestExtras(guestId) {
      if (this.isPastEvent) return;
      this.extrasDialog.guestId = guestId;
      this.extrasDialog.saving = false;
      this.extrasDialog.visible = true;
    },
    async updateGuestCheckedInExtras(count) {
      const guest = this.extrasDialogGuest;
      if (!guest || this.isPastEvent) return;

      this.extrasDialog.saving = true;
      try {
        await FSGetEventGuest(this.eventID, guest.id).update({
          checkedInExtraGuests: Math.max(0, Math.min(count, guest.totalExtraGuests || 0)),
          modificationTime: FBModule.firestore.Timestamp.fromDate(new Date()),
        });
        this.extrasDialog.visible = false;
      } catch (error) {
        showErrorNotification(error.message);
        console.error(error);
      } finally {
        this.extrasDialog.saving = false;
      }
    },
    // Undo a check-in from the list row, same fields the iOS app clears when a guest goes back to Attending
    async resetGuestCheckIn(guestId) {
      const guest = this.$store.state?.guest?.guests?.find((item) => item?.id === guestId);
      if (!guest || guest.status !== 2 || this.isPastEvent) return;

      // Check-out is tracked by the Android/iOS apps only; on the web a check-in can only be reset to Attending
      try {
        // Plain text only: the guest name is user data and must not be rendered as HTML
        await this.$confirm('Do you want to reset check-in?', guest.fullName, {
          confirmButtonText: 'Reset check-in',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'cancel-button',
          confirmButtonClass: 'confirm-button',
          type: 'warning',
        });
      } catch (cancelled) {
        return;
      }

      const { FieldValue, Timestamp } = FBModule.firestore;

      try {
        await FSGetEventGuest(this.eventID, guest.id).update({
          status: 1,
          checkInTime: FieldValue.delete(),
          checkOutTime: FieldValue.delete(),
          checkInType: FieldValue.delete(),
          checkInDeviceId: FieldValue.delete(),
          modificationTime: Timestamp.fromDate(new Date()),
        });
      } catch (error) {
        showErrorNotification(error.message);
        console.error(error);
      }
    },
    compressPhoto(photoFile) {
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line no-new
        new Compressor(photoFile, {
          quality: 0.95,
          maxWidth: 512,
          success: resolve,
          error: reject,
        });
      });
    },
    async addGuest({ guest, photoFile }) {
      if (this.isPastEvent) return; // Archived events are read-only
      this.modal.loading = true;

      // Uploading photo first, then adding the guest document
      try {
        // Upload photo if available
        let photoStoragePath = '';
        if (photoFile) {
          let compressedPhotoFile = null;

          photoStoragePath = eventGuestStoragePath(this.eventID, `${UUID()}.jpeg`);

          try {
            compressedPhotoFile = await this.compressPhoto(photoFile);
          } catch (error) {
            console.error(error);
          } finally {
            await FBStorageRef.child(photoStoragePath).put(compressedPhotoFile || photoFile);
          }
        }

        const nowTimestamp = FBModule.firestore.Timestamp.fromDate(new Date());
        const docData = {
          ...normalizeAddGuestData(guest),
          ...photoStoragePath && { photoStoragePath },
          // Additional metadata
          ...guest.status === 2 && { checkInType: 3 },
          creationTime: nowTimestamp,
          modificationTime: nowTimestamp,
          createdBy: this.userId,
          source: 3,
          ...guest.status === 1 && { attendingTime: nowTimestamp },
          ...guest.status === 2 && { checkInTime: nowTimestamp },
          ...guest.status === 3 && { checkOutTime: nowTimestamp },
        };

        await FSGetEventGuests(this.eventID).add(docData);

        this.modal.showing = false;
      } catch (error) {
        this.modal.loading = false;
        showErrorNotification(error.message);
        console.error(error);
      }
    },
    async updateGuest({ oldGuestData, guestData, photo }) {
      if (this.isPastEvent) return; // Archived events are read-only
      this.modal.loading = true;

      // Uploading photo first, then adding the guest document
      try {
        let photoStoragePath = oldGuestData.photoStoragePath || '';

        // Delete old photo
        if (oldGuestData.photoStoragePath && photo?.edited) {
          await FBStorageRef.child(oldGuestData.photoStoragePath).delete();
          photoStoragePath = '';
        }

        // Upload photo if available
        if (photo?.file) {
          let compressedPhotoFile = null;
          photoStoragePath = eventGuestStoragePath(this.eventID, `${UUID()}.jpeg`);

          photoStoragePath = eventGuestStoragePath(this.eventID, `${UUID()}.jpeg`);

          try {
            compressedPhotoFile = await this.compressPhoto(photo.file);
          } catch (error) {
            console.error(error);
          } finally {
            await FBStorageRef.child(photoStoragePath).put(compressedPhotoFile || photo.file);
          }
        }

        const nowTimestamp = FBModule.firestore.Timestamp.fromDate(new Date());
        const docData = {
          ...prepareGuestDataForFB(cleanUpdateGuestData(oldGuestData, {
            ...guestData,
            photoStoragePath,
          })),
          // Additional metadata
          ...guestData.status === 2 && { checkInType: 3 },
          modificationTime: nowTimestamp,
        };

        // Tracking status change times
        if (guestData.status === 0) { // Status: No-status
          if (guestData.attendingTime) docData.attendingTime = FBModule.firestore.FieldValue.delete();
          if (guestData.checkInTime) docData.checkInTime = FBModule.firestore.FieldValue.delete();
          if (guestData.checkOutTime) docData.checkOutTime = FBModule.firestore.FieldValue.delete();
        } else if (guestData.status === 1) { // Status: Attending
          if (!guestData.attendingTime) docData.attendingTime = nowTimestamp;
          if (guestData.checkInTime) docData.checkInTime = FBModule.firestore.FieldValue.delete();
          if (guestData.checkOutTime) docData.checkOutTime = FBModule.firestore.FieldValue.delete();
        } else if (guestData.status === 2) { // Status: Checked-in
          if (!guestData.checkInTime) docData.checkInTime = nowTimestamp;
          if (guestData.checkOutTime) docData.checkOutTime = FBModule.firestore.FieldValue.delete();
        } else if (guestData.status === 3) { // Status: Checked-out
          if (!guestData.checkOutTime) docData.checkOutTime = nowTimestamp;
        }

        await FSGetEventGuest(this.eventID, oldGuestData.id).update(docData);

        this.modal.showing = false;
      } catch (error) {
        this.modal.loading = false;
        showErrorNotification(error.message);
        console.error(error);
      }
    },
    async deleteGuest({ guestId, photoStoragePath }) {
      if (this.isPastEvent) return; // Archived events are read-only
      try {
        const deletionPromises = [];

        if (photoStoragePath) deletionPromises.push(FBStorageRef.child(photoStoragePath).delete());
        deletionPromises.push(FSGetEventGuest(this.eventID, guestId).delete());

        await Promise.all(deletionPromises);

        this.modal.showing = false;
      } catch (error) {
        showErrorNotification(error.message);
        console.error(error);
      }
    },
  },
  beforeDestroy() {
    // Unsubscribe all unnecessary realtime listeners
    if (this.unsubUserEvent !== null) this.unsubUserEvent();
    if (this.unsubEventMember) this.unsubEventMember();
    if (this.unsubUserTeamMembers !== null) this.unsubUserTeamMembers();
    if (this.unsubEventGuestsRef !== null) this.unsubEventGuestsRef();
    if (this.unwatchEventFirstNameFirstOrder !== null) this.unwatchEventFirstNameFirstOrder();

    // Unregister VueX modules that aren't necessary anymore
    // Resets the state of guests, guest filters, selected filters, export and import
    if (this.$store.state.guest) this.$store.unregisterModule('guest');

    this.updateCurrentNavigationTitleBase('');
  },
};
</script>

<style scoped>
.fab-icon {
  @apply w-30 !important;
  @apply h-30;
}

.el-button + .el-button {
  @apply ml-0;
}
</style>
